import AddRounded from '@mui/icons-material/AddRounded';
import LocalShippingRounded from '@mui/icons-material/LocalShippingRounded';
import ShoppingBagRounded from '@mui/icons-material/ShoppingBagRounded';
import { Box, Button, Card, Tab, Tabs, Typography } from '@mui/material';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router';
import { api, unwrap } from '../../../api/client';
import { useAuth } from '../../../auth/AuthProvider';
import { DataTable, type Column } from '../../../components/DataTable';
import { Money, Qty } from '../../../components/Money';
import { StatusChip } from '../../../components/StatusChip';
import { EmptyState, PageHeader } from '../../../components/states';
import { businessDate } from '../../../lib/format';
import { useShop } from '../../../shop/ShopProvider';
import { ErrorState } from '../components/DocParts';
import { ListToolbar } from '../components/ListToolbar';
import { salesKeys, salesOrderStatuses, type DeliveryRow, type SalesOrderRow } from '../lib/api';
import { apiFilters, useListParams, type ListParams } from '../lib/useListParams';
import { humanize } from '../../../components/StatusChip';
import { useExportSelection } from '../../dataio/useExportSelection';
import { filesColumn, useAttachmentCounts } from '../../attachments';

const ORDER_STATUSES = salesOrderStatuses.map((s) => ({ value: s, label: humanize(s) }));
const DELIVERY_STATUSES = [
  { value: 'POSTED', label: 'Posted' },
  { value: 'REVERSED', label: 'Reversed' },
];

function OrdersTable({
  params,
  set,
}: {
  params: ListParams;
  set: (p: Partial<ListParams>) => void;
}) {
  const exportSelection = useExportSelection('sales_order');
  const { can } = useAuth();
  const { format } = useShop();
  const navigate = useNavigate();
  const status = (salesOrderStatuses as readonly string[]).includes(params.status)
    ? (params.status as (typeof salesOrderStatuses)[number])
    : undefined;
  const list = useQuery({
    queryKey: [...salesKeys.orders, 'list', params],
    queryFn: () =>
      unwrap(
        api.GET('/sales-orders', {
          params: { query: { ...apiFilters(params), ...(status ? { status } : {}) } },
        }),
      ),
    placeholderData: keepPreviousData,
  });
  const filtered = !!(params.q || params.status || params.from || params.to);
  const fileCounts = useAttachmentCounts(
    'SALES_ORDER',
    (list.data?.data ?? []).map((r) => r.id),
  );
  const columns: Column<SalesOrderRow>[] = [
    {
      key: 'number',
      header: 'Order',
      render: (r) => (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 650 }}>
            {r.number}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: { sm: 'none' } }}>
            {r.contact.name}
          </Typography>
        </Box>
      ),
    },
    {
      key: 'date',
      header: 'Date',
      render: (r) => businessDate(r.docDate, format),
      hideBelow: 'md',
    },
    { key: 'customer', header: 'Customer', render: (r) => r.contact.name, hideBelow: 'sm' },
    {
      key: 'expected',
      header: 'Expected',
      render: (r) => businessDate(r.expectedDate, format),
      hideBelow: 'lg',
    },
    { key: 'status', header: 'Status', render: (r) => <StatusChip status={r.status} /> },
    {
      key: 'total',
      header: 'Total',
      align: 'right',
      render: (r) => <Money value={r.total} strong />,
    },
  ];
  if (list.isError) return <ErrorState error={list.error} onRetry={() => void list.refetch()} />;
  return (
    <>
      <DataTable
        {...exportSelection.table}
        columns={[...columns, ...filesColumn<SalesOrderRow>(fileCounts)]}
        rows={list.data?.data}
        rowKey={(r) => r.id}
        loading={list.isLoading}
        page={list.data ? { ...list.data.page, page: params.page } : undefined}
        onPageChange={(page) => set({ page })}
        onPageSizeChange={(pageSize) => set({ pageSize })}
        onRowClick={(r) => void navigate(`/sales/orders/${r.id}`)}
        toolbar={
          <ListToolbar
            params={params}
            set={set}
            statuses={ORDER_STATUSES}
            placeholder="Search number or customer…"
          />
        }
        empty={
          filtered ? (
            <EmptyState
              icon={<ShoppingBagRounded />}
              title="No orders match"
              body="Try another search or clear the filters."
              action={{
                label: 'Clear filters',
                onClick: () => set({ q: '', status: '', from: '', to: '' }),
              }}
            />
          ) : (
            <EmptyState
              icon={<ShoppingBagRounded />}
              title="No sales orders yet"
              body="Take an order now, reserve the stock, and deliver and invoice it when the customer is ready."
              action={
                can('sales.create')
                  ? { label: 'New order', onClick: () => void navigate('/sales/orders/new') }
                  : undefined
              }
            />
          )
        }
      />
      {exportSelection.dialog}
    </>
  );
}

function DeliveriesTable({
  params,
  set,
}: {
  params: ListParams;
  set: (p: Partial<ListParams>) => void;
}) {
  const { format } = useShop();
  const navigate = useNavigate();
  const status =
    params.status === 'POSTED' || params.status === 'REVERSED' ? params.status : undefined;
  const list = useQuery({
    queryKey: [...salesKeys.deliveries, 'list', params],
    queryFn: () =>
      unwrap(
        api.GET('/deliveries', {
          params: { query: { ...apiFilters(params), ...(status ? { status } : {}) } },
        }),
      ),
    placeholderData: keepPreviousData,
  });
  const filtered = !!(params.q || params.status || params.from || params.to);
  const fileCounts = useAttachmentCounts(
    'DELIVERY',
    (list.data?.data ?? []).map((r) => r.id),
  );
  const columns: Column<DeliveryRow>[] = [
    {
      key: 'number',
      header: 'Delivery',
      render: (r) => (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 650 }}>
            {r.number}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: { sm: 'none' } }}>
            {r.contact.name}
          </Typography>
        </Box>
      ),
    },
    {
      key: 'date',
      header: 'Date',
      render: (r) => businessDate(r.docDate, format),
      hideBelow: 'md',
    },
    { key: 'customer', header: 'Customer', render: (r) => r.contact.name, hideBelow: 'sm' },
    { key: 'from', header: 'From', render: (r) => r.location.name, hideBelow: 'lg' },
    {
      key: 'source',
      header: 'Source',
      render: (r) => (r.salesOrderId ? 'Sales order' : 'Counter sale'),
      hideBelow: 'md',
    },
    {
      key: 'lines',
      header: 'Lines',
      align: 'right',
      render: (r) => <Qty value={r.lineCount} />,
      hideBelow: 'sm',
    },
    { key: 'status', header: 'Status', render: (r) => <StatusChip status={r.status} /> },
  ];
  if (list.isError) return <ErrorState error={list.error} onRetry={() => void list.refetch()} />;
  return (
    <DataTable
      columns={[...columns, ...filesColumn<DeliveryRow>(fileCounts)]}
      rows={list.data?.data}
      rowKey={(r) => r.id}
      loading={list.isLoading}
      page={list.data ? { ...list.data.page, page: params.page } : undefined}
      onPageChange={(page) => set({ page })}
      onPageSizeChange={(pageSize) => set({ pageSize })}
      onRowClick={(r) => void navigate(`/sales/deliveries/${r.id}`)}
      toolbar={
        <ListToolbar
          params={params}
          set={set}
          statuses={DELIVERY_STATUSES}
          placeholder="Search number or customer…"
        />
      }
      empty={
        <EmptyState
          icon={<LocalShippingRounded />}
          title={filtered ? 'No deliveries match' : 'No deliveries yet'}
          body={
            filtered
              ? 'Try another search or clear the filters.'
              : 'Deliveries appear here when goods leave — from an order or a counter sale.'
          }
          action={
            filtered
              ? {
                  label: 'Clear filters',
                  onClick: () => set({ q: '', status: '', from: '', to: '' }),
                }
              : undefined
          }
        />
      }
    />
  );
}

/** Orders and deliveries, as two tabs (/sales/orders and /sales/deliveries). */
export function OrdersPage() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [params, set] = useListParams();
  const tab = location.pathname.startsWith('/sales/deliveries') ? 'deliveries' : 'orders';
  return (
    <Box>
      <PageHeader
        title="Orders & deliveries"
        subtitle="Orders reserve stock; deliveries move it out to the customer."
        actions={
          can('sales.create') && (
            <Button
              variant="contained"
              startIcon={<AddRounded />}
              onClick={() => void navigate('/sales/orders/new')}
            >
              New order
            </Button>
          )
        }
      />
      <Card sx={{ mb: 2, px: 1.5, display: 'inline-flex', maxWidth: '100%' }}>
        <Tabs
          value={tab}
          onChange={(_, v: string) =>
            void navigate(v === 'orders' ? '/sales/orders' : '/sales/deliveries')
          }
        >
          <Tab
            value="orders"
            label="Sales orders"
            icon={<ShoppingBagRounded />}
            iconPosition="start"
            sx={{ minHeight: 52 }}
          />
          <Tab
            value="deliveries"
            label="Deliveries"
            icon={<LocalShippingRounded />}
            iconPosition="start"
            sx={{ minHeight: 52 }}
          />
        </Tabs>
      </Card>
      {tab === 'orders' ? (
        <OrdersTable params={params} set={set} />
      ) : (
        <DeliveriesTable params={params} set={set} />
      )}
    </Box>
  );
}
